import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  addYears,
  differenceInMinutes,
  endOfDay,
  startOfDay,
  startOfYear,
} from 'date-fns'
import { format } from '@/utils/date-fns'
import { Minus, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import type { DateRange } from 'react-day-picker'
import type { FilterDateRange } from '@/utils/date/last-days-range'
import { getLastDaysRange, isValidLastDays } from '@/utils/date/last-days-range'
import { asString, PERMISSIONS, usePermissions, useUser } from '@/utils'

import {
  DatePicker,
  Field,
  FieldLabel,
  ModuleWrapper,
  UserCombobox,
} from '@/components'
import type { GanttFeature } from '@/components/kibo-ui/gantt'
import {
  GanttFeatureList,
  GanttFeatureRow,
  GanttHeader,
  GanttMarker,
  GanttProvider,
  GanttSidebar,
  GanttSidebarItem,
  GanttTimeline,
} from '@/components/kibo-ui/gantt'
import { PageType } from '@/modules/common/consts'
import { InfringementDetailModal } from '@/modules/infringements/components'
import {
  SEVERITY_COLOR,
  SEVERITY_COLOR_FALLBACK,
} from '@/modules/infringements/consts'
import { INFRINGEMENT_MARKERS_QUERY } from '@/modules/infringements/queries'
import { WORK_PERIOD_SEGMENTS_QUERY } from '@/modules/salary/queries'
import { WorkPeriodActivitiesModal } from './work-period-activities-modal'

type RowKey = 'workPeriod' | 'workPeriodRest'

const SHIFT_GANTT_ROWS: RowKey[] = ['workPeriod', 'workPeriodRest']

// EU 561/2006 thresholds.
const WEEKLY_REST_MIN_MINUTES = 24 * 60
const DAILY_REST_MIN_MINUTES = 9 * 60

// Discrete zoom stops (percent)
const ZOOM_LEVELS = [
  0.1, 0.25, 0.5, 1, 2, 3, 5, 7, 10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 75,
  100, 200, 400,
]
const ZOOM_MIN = ZOOM_LEVELS[0]!
const ZOOM_MAX = ZOOM_LEVELS[ZOOM_LEVELS.length - 1]!

const FETCH_POLICY = 'cache-and-network'

function prevZoom(z: number): number {
  for (let i = ZOOM_LEVELS.length - 1; i >= 0; i--)
    if (ZOOM_LEVELS[i]! < z) return ZOOM_LEVELS[i]!
  return ZOOM_MIN
}

function nextZoom(z: number): number {
  for (const lvl of ZOOM_LEVELS) {
    if (lvl > z) return lvl
  }
  return ZOOM_MAX
}

/**
 * Renders a work-period / rest gantt for the selected driver + date range.
 * Default range is the current year (Jan 1 → today)
 *
 * Data sources:
 *   - `workPeriodSegments` — lean WorkPeriod projection for work-period bars
 *   - `infringementMarkers` — lean infringement projection for vertical line markers + click-through to the detail popup
 */
export function InfringementAnalytics() {
  const translations = useTranslate({
    title: 'infringements.analytics.title',
    driver: 'common.driver',
    period: 'common.period',
    selectDriver: 'infringements.analytics.select-driver',
    emptyState: 'infringements.analytics.empty-state',
    zoomIn: 'common.zoom-in',
    zoomOut: 'common.zoom-out',
    shiftOverview: 'infringements.analytics.shift-overview',
    shiftOverviewSubV2: 'infringements.analytics.shift-overview-sub-v2',
    shifts: 'infringements.analytics.shifts',
    shiftsRest: 'infringements.analytics.shifts-rest',
  })
  const [selectedInfringementId, setSelectedInfringementId] = useState<
    string | null
  >(null)
  const [selectedWorkPeriod, setSelectedWorkPeriod] = useState<{
    startAt: Date
    endAt: Date
  } | null>(null)

  const today = new Date()
  const defaultDateRange: DateRange = {
    from: startOfYear(today),
    to: today,
  }
  const me = useUser()
  const { formValues: filter, updateForm } = useForm<{
    userId: string
    dateRange: DateRange & Pick<FilterDateRange, 'lastDays'>
    zoom: number
  }>({
    values: {
      userId: '',
      dateRange: defaultDateRange,
      zoom: 3,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'infringement-analytics-filter',
        sessionStorage: true,
        transformValues: {
          userId: asString,
          dateRange: value => {
            if (
              value &&
              typeof value === 'object' &&
              !Array.isArray(value) &&
              'from' in value &&
              'to' in value
            ) {
              if ('lastDays' in value && isValidLastDays(value.lastDays))
                return getLastDaysRange(value.lastDays)

              const from = new Date(value.from as string)
              const to = new Date(value.to as string)
              const twoYearsMs = 2 * 365 * 24 * 60 * 60 * 1000
              const now = today.getTime()
              const sane =
                !Number.isNaN(from.getTime()) &&
                !Number.isNaN(to.getTime()) &&
                Math.abs(from.getTime() - now) <= twoYearsMs &&
                Math.abs(to.getTime() - now) <= twoYearsMs

              if (sane) return { from, to }
            }
            return defaultDateRange
          },
          zoom: value => {
            const n = typeof value === 'number' ? value : Number(value)
            return Number.isFinite(n) ? n : 3
          },
        },
      },
    },
  })

  const { hasPermissions } = usePermissions()
  const canViewOthersInfringements = hasPermissions(
    PERMISSIONS.infringements.view.othersinfringement
  )
  const userId = canViewOthersInfringements ? filter.userId : me.id
  const dateRange = filter.dateRange
  const ganttZoom = filter.zoom
  const setUserId = (value: string) => updateForm({ userId: value })
  const setDateRange = (value: DateRange & Pick<FilterDateRange, 'lastDays'>) =>
    updateForm({ dateRange: value })
  const setGanttZoom = (updater: number | ((prev: number) => number)) =>
    updateForm({
      zoom: typeof updater === 'function' ? updater(ganttZoom) : updater,
    })

  const skipQueries = !userId || !dateRange.from || !dateRange.to
  const dateStart = dateRange.from
    ? startOfDay(dateRange.from).toISOString()
    : null
  const dateEnd = dateRange.to ? endOfDay(dateRange.to).toISOString() : null

  const { data: markersData, loading: markersLoading } = useQuery(
    INFRINGEMENT_MARKERS_QUERY,
    {
      skip: skipQueries,
      fetchPolicy: FETCH_POLICY,
      nextFetchPolicy: 'cache-first',
      variables: {
        userId,
        dateStart: dateStart!,
        dateEnd: dateEnd!,
      },
    }
  )

  const { data: shiftsData, loading: shiftsLoading } = useQuery(
    WORK_PERIOD_SEGMENTS_QUERY,
    {
      skip: skipQueries,
      fetchPolicy: FETCH_POLICY,
      nextFetchPolicy: 'cache-first',
      variables: {
        userId,
        dateStart: dateStart!,
        dateEnd: dateEnd!,
      },
    }
  )

  const loading = markersLoading || shiftsLoading

  const featuresByRow = useMemo<Record<RowKey, GanttFeature[]>>(() => {
    const byRow: Record<RowKey, GanttFeature[]> = {
      workPeriod: [],
      workPeriodRest: [],
    }

    const formatShiftTooltip = (start: Date, end: Date) => {
      const minutes = Math.max(0, differenceInMinutes(end, start))
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      const duration = `${hours}h ${String(mins).padStart(2, '0')}m`
      const sameDay =
        start.getFullYear() === end.getFullYear() &&
        start.getMonth() === end.getMonth() &&
        start.getDate() === end.getDate()
      return (
        <div className="flex flex-col gap-1 text-left text-xs">
          {sameDay ? (
            <>
              <div className="font-semibold">{format(start, 'PP')}</div>
              <div className="opacity-80">
                {format(start, 'HH:mm')} → {format(end, 'HH:mm')}
              </div>
            </>
          ) : (
            // Multi-day spans — arrow trails the start row so it reads
            // top-to-bottom as a continuous range.
            <>
              <div>
                <span className="font-semibold">{format(start, 'PP')}</span>{' '}
                {format(start, 'HH:mm')} <span className="opacity-70">→</span>
              </div>
              <div>
                <span className="font-semibold">{format(end, 'PP')}</span>{' '}
                {format(end, 'HH:mm')}
              </div>
            </>
          )}
          <div className="opacity-70">{duration}</div>
        </div>
      )
    }

    const shifts = shiftsData?.workPeriodSegments ?? []
    const sortedShifts = [...shifts].sort((a, b) =>
      a.datetimeStart.localeCompare(b.datetimeStart)
    )

    for (const s of sortedShifts) {
      const color = '#CBD5E1'
      const start = new Date(s.datetimeStart)
      const end = new Date(s.datetimeEnd)
      byRow.workPeriod.push({
        id: `wp-${s.datetimeStart}`,
        name: '',
        startAt: start,
        endAt: end,
        tooltipContent: formatShiftTooltip(start, end),
        status: {
          id: 'shift',
          name: 'Shift',
          color,
        },
      })
    }

    // Rest row = every gap between consecutive work periods. Color tiered by
    // EU 561/2006 thresholds so weekly rest reads visually different
    // from daily rest at a glance.
    for (let i = 0; i < sortedShifts.length - 1; i++) {
      const prev = sortedShifts[i]!
      const next = sortedShifts[i + 1]!
      const gapStart = new Date(prev.datetimeEnd)
      const gapEnd = new Date(next.datetimeStart)
      const gapMinutes = differenceInMinutes(gapEnd, gapStart)
      // Skip degenerate gaps (back-to-back or overlapping shifts).
      if (gapMinutes <= 0) continue
      const restColor =
        gapMinutes >= WEEKLY_REST_MIN_MINUTES
          ? '#00848F'
          : gapMinutes >= DAILY_REST_MIN_MINUTES
          ? '#4C9CF1'
          : '#CBD5E1'
      byRow.workPeriodRest.push({
        id: `wp-rest-${prev.datetimeStart}-${next.datetimeStart}`,
        name: '',
        startAt: gapStart,
        endAt: gapEnd,
        tooltipContent: formatShiftTooltip(gapStart, gapEnd),
        status: {
          id: 'shift-rest',
          name: 'Rest',
          color: restColor,
        },
      })
    }

    return byRow
  }, [shiftsData])

  const rowLabels: Record<RowKey, string> = {
    workPeriod: translations.shifts,
    workPeriodRest: translations.shiftsRest,
  }

  const markers = markersData?.infringementMarkers ?? []

  const datetimeStart = dateRange.from ?? startOfYear(new Date())
  const datetimeEnd = dateRange.to ?? new Date()

  return (
    <ModuleWrapper
      title={translations.title}
      page={PageType.INFRINGEMENTS}
      loading={loading}
    >
      <div className="mb-6 flex flex-wrap items-end gap-4 rounded-lg bg-white p-4 shadow-card">
        {canViewOthersInfringements && (
          <Field>
            <FieldLabel>{translations.driver}</FieldLabel>
            <UserCombobox
              mode="single"
              value={userId}
              clearable
              placeholder={translations.selectDriver}
              onChange={value => setUserId(value)}
              className="min-w-[280px]"
            />
          </Field>
        )}

        <Field>
          <FieldLabel>{translations.period}</FieldLabel>
          <DatePicker
            mode="range"
            value={dateRange}
            format="PP"
            onChange={value => {
              if (!value?.from || !value.to) return
              // Cap the range at one year. Beyond that the gantt
              // renders at sub-pixel scale and the WorkPeriod payload
              // blows up — neither readable nor performant.
              const maxTo = addYears(value.from, 1)
              const to = value.to > maxTo ? maxTo : value.to
              setDateRange({
                from: value.from,
                to,
                lastDays: to === value.to ? value.lastDays : undefined,
              })
            }}
          />
        </Field>
      </div>

      {!userId && (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow-card">
          {translations.emptyState}
        </div>
      )}

      {userId && (
        <section className="rounded-lg bg-white p-4 shadow-card">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold">
                {translations.shiftOverview}
              </h2>
              <span className="text-xs text-gray-500">
                {translations.shiftOverviewSubV2}
              </span>
            </div>
            <div className="flex items-center gap-1 rounded-lg border border-gray-200">
              <button
                className="flex h-8 w-8 items-center justify-center text-gray-500 disabled:opacity-40"
                disabled={ganttZoom <= ZOOM_MIN}
                onClick={() => setGanttZoom(z => prevZoom(z))}
                type="button"
                aria-label={translations.zoomOut}
              >
                <Minus size={14} />
              </button>
              <span className="min-w-[36px] text-center text-[11px] font-semibold text-gray-900">
                {ganttZoom}%
              </span>
              <button
                className="flex h-8 w-8 items-center justify-center text-gray-500 disabled:opacity-40"
                disabled={ganttZoom >= ZOOM_MAX}
                onClick={() => setGanttZoom(z => nextZoom(z))}
                type="button"
                aria-label={translations.zoomIn}
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
          <GanttProvider
            className="border pb-5"
            range="daily"
            zoom={ganttZoom}
            datetimeStart={datetimeStart}
            datetimeEnd={datetimeEnd}
          >
            <GanttSidebar
              className="!w-28 sm:!w-56 md:!w-64"
              compact={isMobile}
            >
              {SHIFT_GANTT_ROWS.map(row => {
                const rowFeatures = featuresByRow[row]
                return (
                  <GanttSidebarItem
                    feature={{
                      id: row,
                      name: rowLabels[row],
                      startAt: rowFeatures[0]?.startAt ?? datetimeStart,
                      endAt:
                        rowFeatures[rowFeatures.length - 1]?.endAt ??
                        datetimeEnd,
                      hideDuration: true,
                    }}
                    key={row}
                  />
                )
              })}
            </GanttSidebar>
            <GanttTimeline>
              <GanttHeader />
              {markers.map(m => (
                <GanttMarker
                  key={m.id}
                  id={m.id}
                  date={new Date(m.infringementTimestamp)}
                  label={m.categoryIdentifier || 'Infringement'}
                  color={SEVERITY_COLOR[m.severity] || SEVERITY_COLOR_FALLBACK}
                  onClick={() => setSelectedInfringementId(m.id)}
                />
              ))}
              <GanttFeatureList>
                {SHIFT_GANTT_ROWS.map(row => (
                  <GanttFeatureRow
                    className="min-w-full"
                    features={featuresByRow[row]}
                    handleViewFeature={() => void 0}
                    key={row}
                    onFeatureClick={
                      row === 'workPeriod'
                        ? feature =>
                            setSelectedWorkPeriod({
                              startAt: feature.startAt,
                              endAt: feature.endAt,
                            })
                        : undefined
                    }
                  />
                ))}
              </GanttFeatureList>
            </GanttTimeline>
          </GanttProvider>
        </section>
      )}
      <InfringementDetailModal
        open={selectedInfringementId !== null}
        infringementId={selectedInfringementId}
        onClose={() => setSelectedInfringementId(null)}
      />
      <WorkPeriodActivitiesModal
        open={selectedWorkPeriod !== null}
        userId={userId || null}
        startAt={selectedWorkPeriod?.startAt ?? null}
        endAt={selectedWorkPeriod?.endAt ?? null}
        onClose={() => setSelectedWorkPeriod(null)}
      />
    </ModuleWrapper>
  )
}
